import { useCallback, useEffect, useState } from "react"
import { Navigate, useLocation } from "react-router"
import { UserContext, type CurrentUserData } from "../contexts/UserContext"
import { PrefetchedOrgsContext, type OrganizationWithAccounts } from "../contexts/organizations"

type Status = "loading" | "authenticated" | "no_organization" | "unauthenticated"

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<Status>("loading")
  const [userData, setUserData] = useState<CurrentUserData>({ user: null, role: null, hasOrganization: false, updateUser: () => {} })
  const [prefetchedOrgs, setPrefetchedOrgs] = useState<OrganizationWithAccounts[] | null>(null)
  const [initialCheckDone, setInitialCheckDone] = useState(false)
  const location = useLocation()

  const updateUser = useCallback((patch: { name?: string; email?: string }) => {
    setUserData(prev => ({
      ...prev,
      user: prev.user ? { ...prev.user, ...patch } : prev.user,
    }))
  }, [])

  useEffect(() => {
    fetch("/users/me", { headers: { Accept: "application/json" } })
      .then(async res => {
        if (!res.ok) {
          setStatus("unauthenticated")
          setInitialCheckDone(true)
          return
        }
        const data = await res.json()
        setUserData({
          user: data.user ?? null,
          role: data.role ?? null,
          hasOrganization: data.has_organization,
          updateUser,
        })
        if (Array.isArray(data.organizations)) {
          setPrefetchedOrgs(data.organizations)
        }
        setStatus(data.has_organization ? "authenticated" : "no_organization")
        setInitialCheckDone(true)
      })
      .catch(() => { setStatus("unauthenticated"); setInitialCheckDone(true) })
  }, [location.pathname, updateUser])

  // 初回チェック完了まではブロック。以降は前回の認証状態を維持しながら再チェック
  if (!initialCheckDone) return null
  if (status === "unauthenticated") return <Navigate to="/login" replace />

  // /onboarding 自体は組織未所属でもアクセス可能（無限ループ防止）
  if (status === "no_organization" && location.pathname !== "/onboarding") {
    const pendingRedirect = sessionStorage.getItem("oauth_post_redirect")
    if (pendingRedirect) {
      sessionStorage.removeItem("oauth_post_redirect")
      return <Navigate to={pendingRedirect} replace />
    }
    return <Navigate to="/onboarding" replace />
  }

  // 組織所属済みなら /onboarding に留まる理由はないのでホームへ
  if (status === "authenticated" && location.pathname === "/onboarding") {
    return <Navigate to="/" replace />
  }

  return (
    <UserContext.Provider value={userData}>
      <PrefetchedOrgsContext.Provider value={prefetchedOrgs}>
        {children}
      </PrefetchedOrgsContext.Provider>
    </UserContext.Provider>
  )
}
